// LeasingAgentPortal — public magic-link page for leasing agents (Raza's
// team, Irfan's staff, etc.) at /leasing/:token. Shows every vacant unit
// the agent is allowed to show, with entry code, alarm code, lockbox loc,
// wifi + parking notes, and per-unit Report / Check-in buttons. Full
// two-way message thread with PMR. Reduces the "text me the code" chatter
// that eats owner + PMR time.
import { useEffect, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import RentEstimateChip from "@/components/RentEstimateChip";
import {
  Key, Bell, Package, WifiHigh, Car, Wrench, ChatText, CheckCircle,
  Warning, MapPin, PaperPlaneTilt, Camera, ClipboardText, X,
} from "@phosphor-icons/react";

const BASE = process.env.REACT_APP_BACKEND_URL;

export default function LeasingAgentPortal() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [openUnit, setOpenUnit] = useState(null);
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${BASE}/api/public/leasing/${token}`);
      setData(r.data);
    } catch (e) { setError(e.response?.data?.detail || "Link not available"); }
  }, [token]);
  useEffect(() => { load(); }, [load]);

  async function sendMessage() {
    if (!message.trim()) return;
    setBusy(true);
    try {
      await axios.post(`${BASE}/api/public/leasing/${token}/message`, { text: message });
      setMessage("");
      await load();
    } catch (e) { alert(e.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  }

  if (error) return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6" data-testid="leasing-error">
      <div className="bg-white rounded-2xl p-8 max-w-md text-center shadow-sm">
        <Warning size={48} className="text-red-500 mx-auto mb-3"/>
        <div className="font-black text-lg mb-1">Link not available</div>
        <div className="text-sm text-slate-500">{error}</div>
      </div>
    </div>
  );
  if (!data) return <div className="min-h-screen flex items-center justify-center text-slate-400" data-testid="leasing-loading">Loading…</div>;

  const { agent, units, messages, recent_reports, recent_showings } = data;

  return (
    <div className="min-h-screen bg-slate-50 pb-24" data-testid="leasing-portal">
      {/* Header */}
      <div className="bg-gradient-to-r from-cyan-600 to-cyan-700 text-white p-5 sm:p-6">
        <div className="max-w-3xl mx-auto">
          <div className="text-[10px] font-black uppercase tracking-widest opacity-80">Leasing agent portal</div>
          <h1 className="text-2xl font-black mt-1">Welcome, {agent.name}</h1>
          <div className="text-xs mt-1 opacity-90">
            {agent.scope_name ? `${agent.scope_name} portfolio · ` : ""}
            {units.length} vacant unit{units.length === 1 ? "" : "s"} available to show
          </div>
        </div>
      </div>

      <div className="max-w-3xl mx-auto p-4 sm:p-6 space-y-4">
        {/* Unit cards */}
        {units.length === 0 && (
          <div className="bg-white rounded-2xl p-10 text-center text-slate-500 shadow-sm" data-testid="leasing-no-units">
            No vacant units listed right now. Message the office below if you're expecting one.
          </div>
        )}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {units.map(u => (
            <UnitCard key={u.unit_key} u={u} onOpen={() => setOpenUnit(u)}/>
          ))}
        </div>

        {/* Recent activity */}
        {(recent_reports?.length || recent_showings?.length) ? (
          <div className="bg-white rounded-2xl p-5 shadow-sm mt-4" data-testid="leasing-activity">
            <div className="text-[10px] uppercase font-black tracking-widest text-slate-500 mb-2">Your recent activity</div>
            <div className="space-y-1 text-xs">
              {[...(recent_reports || []).map(r => ({ ...r, _kind: "report", when: r.at })),
                ...(recent_showings || []).map(s => ({ ...s, _kind: "showing", when: s.checked_in_at }))]
                .sort((a, b) => (b.when || "").localeCompare(a.when || ""))
                .slice(0, 12)
                .map((row, i) => (
                  <div key={i} className="flex items-start gap-2 py-1 border-b border-slate-100 last:border-0">
                    {row._kind === "report" ? <Wrench size={12} className="text-orange-500 mt-0.5"/> : <ClipboardText size={12} className="text-cyan-500 mt-0.5"/>}
                    <div className="flex-1 min-w-0">
                      <div className="truncate">
                        {row._kind === "report" ? <span className="font-bold">Report:</span> : <span className="font-bold">Showing:</span>}{" "}
                        {row.title || row.prospect_name || "—"} · <span className="text-slate-500">{row.address} {row.unit ? `#${row.unit}` : ""}</span>
                      </div>
                      <div className="text-slate-400 text-[10px]">{row.when?.slice(0, 16).replace("T", " ")}</div>
                    </div>
                  </div>
                ))}
            </div>
          </div>
        ) : null}

        {/* Messages */}
        <div className="bg-white rounded-2xl p-5 shadow-sm" data-testid="leasing-thread">
          <div className="text-[10px] uppercase font-black tracking-widest text-slate-500 mb-2 flex items-center gap-1"><ChatText size={12}/> Message office</div>
          <div className="space-y-2 mb-3 max-h-64 overflow-y-auto">
            {(messages || []).map((m, i) => (
              <div key={i}
                className={`text-sm rounded-lg p-2.5 ${m.from === "admin" ? "bg-cyan-50 mr-8 text-cyan-900" : "bg-slate-100 ml-8"}`}
                data-testid={`leasing-msg-${i}`}>
                <div className="text-[10px] uppercase font-black tracking-wider opacity-60">{m.from === "admin" ? "Office" : "You"}</div>
                <div>{m.text}</div>
              </div>
            ))}
            {!(messages || []).length && <div className="text-xs text-slate-400 italic">No messages yet. Say hi!</div>}
          </div>
          <div className="flex gap-2">
            <input value={message} onChange={e => setMessage(e.target.value)} placeholder="Message the office…"
              className="flex-1 border border-slate-300 rounded-lg px-3 py-2 text-sm" data-testid="leasing-msg-input"/>
            <button disabled={busy || !message.trim()} onClick={sendMessage}
              className="bg-slate-900 text-white rounded-lg px-4 disabled:opacity-40" data-testid="leasing-msg-send">
              <PaperPlaneTilt weight="fill" size={16}/>
            </button>
          </div>
        </div>

        <div className="text-center text-[10px] text-slate-400 mt-4">
          Property Management Radar · secure leasing-agent portal
        </div>
      </div>

      {openUnit && <UnitDrawer token={token} u={openUnit} onClose={() => setOpenUnit(null)} onDone={load}/>}
    </div>
  );
}

function fmtRent(cents) { if (!cents) return ""; return `$${(cents / 100).toLocaleString()}/mo`; }

function UnitCard({ u, onOpen }) {
  return (
    <button onClick={onOpen} className="text-left bg-white rounded-2xl p-4 shadow-sm hover:shadow-md transition"
      data-testid={`leasing-unit-${u.unit_key}`}>
      <div className="flex items-start gap-2">
        <MapPin weight="fill" size={16} className="text-cyan-600 mt-0.5"/>
        <div className="flex-1 min-w-0">
          <div className="font-black text-sm text-slate-900 truncate">{u.address}</div>
          {u.unit && <div className="text-xs text-slate-500">Unit {u.unit}</div>}
        </div>
      </div>
      <div className="mt-2 flex items-center gap-2 text-[11px] flex-wrap">
        {u.listing_rent_cents ? (
          <RentEstimateChip
            rentCents={u.listing_rent_cents}
            source={u.listing_rent_source}
            rationale={u.listing_rent_rationale}
            rangeLowCents={u.listing_rent_range_low_cents}
            rangeHighCents={u.listing_rent_range_high_cents}
            confidence={u.listing_rent_confidence}
            neighborhood={u.listing_neighborhood}
            updatedAt={u.listing_rent_updated_at}
            size="sm"
          />
        ) : null}
        {u.listing_bedrooms ? <span className="text-slate-600">· {u.listing_bedrooms}bd</span> : null}
        {u.listing_bathrooms ? <span className="text-slate-600">· {u.listing_bathrooms}ba</span> : null}
        {u.listing_sqft ? <span className="text-slate-600">· {u.listing_sqft} sf</span> : null}
      </div>
      <div className="mt-2 flex items-center gap-1.5 text-[10px] font-black uppercase tracking-widest text-slate-500">
        {u.entry_code && <span className="bg-cyan-50 text-cyan-700 rounded px-1.5 py-0.5">Code</span>}
        {u.alarm_code && <span className="bg-orange-50 text-orange-700 rounded px-1.5 py-0.5">Alarm</span>}
        {u.lockbox_location && <span className="bg-slate-100 text-slate-700 rounded px-1.5 py-0.5">Lockbox</span>}
        {u.wifi_ssid && <span className="bg-indigo-50 text-indigo-700 rounded px-1.5 py-0.5">WiFi</span>}
      </div>
    </button>
  );
}

function UnitDrawer({ token, u, onClose, onDone }) {
  const [showing, setShowing] = useState(null);   // set after check-in
  const [reportOpen, setReportOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [prospect, setProspect] = useState("");

  async function checkIn() {
    setBusy(true);
    try {
      const r = await axios.post(`${BASE}/api/public/leasing/${token}/check-in`,
        { unit_key: u.unit_key, prospect_name: prospect });
      setShowing(r.data.showing);
    } catch (e) { alert(e.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  }
  async function checkOut() {
    if (!showing) return;
    setBusy(true);
    try {
      await axios.post(`${BASE}/api/public/leasing/${token}/check-out/${showing.id}`);
      setShowing(null);
      onDone && onDone();
      onClose();
    } catch (e) { alert(e.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  }

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-end sm:items-center justify-center" onClick={onClose}>
      <div className="bg-white w-full sm:max-w-lg sm:rounded-2xl max-h-[92vh] overflow-y-auto"
        onClick={e => e.stopPropagation()} data-testid="leasing-unit-drawer">
        <div className="p-5 border-b border-slate-100 sticky top-0 bg-white z-10 flex items-start gap-2">
          <div className="flex-1 min-w-0">
            <div className="text-[10px] uppercase font-black tracking-widest text-cyan-600">Unit access</div>
            <div className="font-black text-lg">{u.address}</div>
            {u.unit && <div className="text-xs text-slate-500">Unit {u.unit}</div>}
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600" data-testid="leasing-drawer-close"><X size={20}/></button>
        </div>

        <div className="p-5 space-y-4">
          {/* Codes */}
          {u.entry_code && (
            <CodeRow icon={<Key size={18}/>} label="Entry code" value={u.entry_code} colorClass="bg-cyan-50 text-cyan-800" testid="leasing-code-entry"/>
          )}
          {(u.alarm_code || u.alarm_disarm_code) && (
            <div className="bg-orange-50 rounded-xl p-3" data-testid="leasing-code-alarm">
              <div className="flex items-center gap-2 text-orange-900 font-black text-sm mb-1"><Bell size={16}/> Alarm</div>
              {u.alarm_code && <div className="text-sm"><span className="text-slate-500 text-xs">Code:</span> <span className="font-mono font-black text-lg">{u.alarm_code}</span></div>}
              {u.alarm_disarm_code && u.alarm_disarm_code !== u.alarm_code && <div className="text-sm mt-1"><span className="text-slate-500 text-xs">Disarm:</span> <span className="font-mono font-black text-lg">{u.alarm_disarm_code}</span></div>}
              {u.alarm_notes && <div className="text-xs text-orange-700 mt-1 italic">{u.alarm_notes}</div>}
            </div>
          )}
          {u.lockbox_location && (
            <CodeRow icon={<Package size={18}/>} label="Lockbox" value={u.lockbox_location} colorClass="bg-slate-50 text-slate-800" testid="leasing-code-lockbox"/>
          )}
          {(u.wifi_ssid || u.wifi_password) && (
            <div className="bg-indigo-50 rounded-xl p-3" data-testid="leasing-code-wifi">
              <div className="flex items-center gap-2 text-indigo-900 font-black text-sm mb-1"><WifiHigh size={16}/> WiFi</div>
              {u.wifi_ssid && <div className="text-sm"><span className="text-slate-500 text-xs">SSID:</span> <span className="font-mono">{u.wifi_ssid}</span></div>}
              {u.wifi_password && <div className="text-sm"><span className="text-slate-500 text-xs">Password:</span> <span className="font-mono">{u.wifi_password}</span></div>}
            </div>
          )}
          {u.parking_notes && (
            <CodeRow icon={<Car size={18}/>} label="Parking" value={u.parking_notes} colorClass="bg-slate-50 text-slate-800" testid="leasing-code-parking"/>
          )}

          {/* Listing meta */}
          {(u.listing_rent_cents || u.listing_bedrooms || u.listing_sqft) ? (
            <div className="bg-slate-50 rounded-xl p-3" data-testid="leasing-listing-meta">
              <div className="text-[10px] uppercase font-black tracking-widest text-slate-500 mb-1">Listing</div>
              <div className="text-sm flex items-center gap-2 flex-wrap">
                {u.listing_rent_cents ? (
                  <RentEstimateChip
                    rentCents={u.listing_rent_cents}
                    source={u.listing_rent_source}
                    rationale={u.listing_rent_rationale}
                    rangeLowCents={u.listing_rent_range_low_cents}
                    rangeHighCents={u.listing_rent_range_high_cents}
                    confidence={u.listing_rent_confidence}
                    neighborhood={u.listing_neighborhood}
                    updatedAt={u.listing_rent_updated_at}
                  />
                ) : null}
                {u.listing_bedrooms ? <span className="text-slate-700"> · {u.listing_bedrooms}bd</span> : null}
                {u.listing_bathrooms ? <span className="text-slate-700"> · {u.listing_bathrooms}ba</span> : null}
                {u.listing_sqft ? <span className="text-slate-700"> · {u.listing_sqft} sf</span> : null}
              </div>
              {u.listing_amenities?.length ? (
                <div className="mt-1 flex flex-wrap gap-1">
                  {u.listing_amenities.map((a, i) => (
                    <span key={i} className="text-[10px] bg-white border border-slate-200 rounded px-1.5 py-0.5">{a}</span>
                  ))}
                </div>
              ) : null}
              {u.listing_notes && <div className="text-xs text-slate-600 mt-1 italic">{u.listing_notes}</div>}
            </div>
          ) : null}

          {/* Photos */}
          {u.listing_photos?.length ? (
            <div>
              <div className="text-[10px] uppercase font-black tracking-widest text-slate-500 mb-1 flex items-center gap-1"><Camera size={12}/> Photos</div>
              <div className="grid grid-cols-3 gap-2">
                {u.listing_photos.map((p, i) => {
                  const src = `${BASE}/api/public/leasing/${token}/photo?path=${encodeURIComponent(p)}`;
                  return (
                    <a key={i} href={src} target="_blank" rel="noreferrer"
                      className="block aspect-square rounded-lg overflow-hidden bg-slate-100">
                      <img src={src} alt="" className="w-full h-full object-cover"/>
                    </a>
                  );
                })}
              </div>
            </div>
          ) : null}

          {/* Actions */}
          {!showing && (
            <div className="pt-2 border-t border-slate-100">
              <div className="text-[10px] uppercase font-black tracking-widest text-slate-500 mb-1">Check in when you arrive</div>
              <input value={prospect} onChange={e => setProspect(e.target.value)}
                placeholder="Prospect name (optional)"
                className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm mb-2" data-testid="leasing-prospect-name"/>
              <button disabled={busy} onClick={checkIn}
                className="w-full bg-emerald-600 hover:bg-emerald-700 disabled:opacity-40 text-white font-black py-2.5 rounded-xl text-sm"
                data-testid="leasing-checkin-btn">
                <CheckCircle weight="fill" size={14} className="inline mr-1"/> Check in
              </button>
            </div>
          )}
          {showing && (
            <div className="pt-2 border-t border-slate-100" data-testid="leasing-checked-in">
              <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-3 text-sm">
                <div className="font-black text-emerald-800 flex items-center gap-1"><CheckCircle weight="fill" size={14}/> Checked in {new Date(showing.checked_in_at).toLocaleTimeString()}</div>
                {showing.prospect_name && <div className="text-xs text-emerald-700 mt-0.5">Showing to {showing.prospect_name}</div>}
              </div>
              <button disabled={busy} onClick={checkOut}
                className="w-full mt-2 bg-slate-900 text-white font-black py-2.5 rounded-xl text-sm disabled:opacity-40"
                data-testid="leasing-checkout-btn">
                Check out
              </button>
            </div>
          )}

          {/* Report button */}
          <button onClick={() => setReportOpen(true)}
            className="w-full border-2 border-orange-500 text-orange-700 hover:bg-orange-50 font-black py-2.5 rounded-xl text-sm"
            data-testid="leasing-report-btn">
            <Wrench size={14} className="inline mr-1"/> Report issue / feedback
          </button>
        </div>

        {reportOpen && (
          <ReportModal token={token} u={u} onClose={() => setReportOpen(false)}
            onDone={() => { setReportOpen(false); onDone && onDone(); }}/>
        )}
      </div>
    </div>
  );
}

function CodeRow({ icon, label, value, colorClass, testid }) {
  return (
    <div className={`rounded-xl p-3 ${colorClass}`} data-testid={testid}>
      <div className="flex items-center gap-2 font-black text-sm mb-1">{icon} {label}</div>
      <div className="font-mono text-lg font-black break-all">{value}</div>
    </div>
  );
}

function ReportModal({ token, u, onClose, onDone }) {
  const [kind, setKind] = useState("maintenance");
  const [title, setTitle] = useState("");
  const [detail, setDetail] = useState("");
  const [priority, setPriority] = useState("normal");
  const [photos, setPhotos] = useState([]);   // array of {path, previewUrl}
  const [uploading, setUploading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);

  async function uploadFiles(files) {
    if (!files?.length) return;
    setUploading(true);
    for (const file of Array.from(files).slice(0, 6 - photos.length)) {
      const fd = new FormData();
      fd.append("file", file);
      try {
        const r = await axios.post(`${BASE}/api/public/leasing/${token}/report-photo`, fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        const path = r.data.path;
        const previewUrl = `${BASE}/api/public/leasing/${token}/report-photo?path=${encodeURIComponent(path)}`;
        setPhotos(prev => [...prev, { path, previewUrl }]);
      } catch (e) { alert(e.response?.data?.detail || `Upload failed: ${file.name}`); }
    }
    setUploading(false);
  }

  async function submit() {
    if (!title.trim()) { alert("Enter a short title"); return; }
    setBusy(true);
    try {
      const r = await axios.post(`${BASE}/api/public/leasing/${token}/report`, {
        unit_key: u.unit_key, kind, title, detail, priority,
        photos: photos.map(p => p.path),
      });
      setDone(r.data.report);
    } catch (e) { alert(e.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  }

  return (
    <div className="fixed inset-0 z-[60] bg-black/60 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl max-w-md w-full p-6 max-h-[92vh] overflow-y-auto" onClick={e => e.stopPropagation()} data-testid="leasing-report-modal">
        {!done ? (
          <>
            <h3 className="text-lg font-black mb-1">Report — {u.address}{u.unit ? ` · Unit ${u.unit}` : ""}</h3>
            <p className="text-xs text-slate-500 mb-3">Tell PMR + the owner what you found. Maintenance items open a real work order.</p>
            <div className="space-y-2">
              <div className="grid grid-cols-4 gap-1">
                {["maintenance", "access", "feedback", "other"].map(k => (
                  <button key={k} onClick={() => setKind(k)}
                    className={`h-9 text-[11px] font-black uppercase tracking-wider rounded ${kind === k ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-600"}`}
                    data-testid={`leasing-report-kind-${k}`}>{k}</button>
                ))}
              </div>
              <input value={title} onChange={e => setTitle(e.target.value)}
                placeholder="Short title (e.g. Kitchen faucet leaks)"
                className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm" data-testid="leasing-report-title"/>
              <textarea value={detail} onChange={e => setDetail(e.target.value)} rows={4}
                placeholder="Details (what you saw, prospect feedback, anything else the owner should know)"
                className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm" data-testid="leasing-report-detail"/>

              {/* Photos */}
              <div className="pt-2">
                <div className="flex items-center justify-between mb-2">
                  <div className="text-[10px] uppercase font-black tracking-widest text-slate-500">Photos <span className="normal-case font-normal text-slate-400">({photos.length}/6 · optional)</span></div>
                </div>
                <div className="grid grid-cols-2 gap-2 mb-2">
                  {/* Camera capture button (mobile) */}
                  <label className={`h-20 rounded-lg border-2 border-dashed border-slate-300 hover:border-cyan-400 flex flex-col items-center justify-center cursor-pointer text-slate-500 hover:text-cyan-600 transition ${(uploading || photos.length >= 6) ? "opacity-40 pointer-events-none" : ""}`} data-testid="leasing-report-camera-label">
                    <Camera size={20}/>
                    <div className="text-[10px] font-black uppercase tracking-widest mt-1">Take photo</div>
                    <input type="file" accept="image/*" capture="environment"
                      className="hidden" onChange={e => uploadFiles(e.target.files)}
                      data-testid="leasing-report-camera-input"/>
                  </label>
                  {/* Gallery picker */}
                  <label className={`h-20 rounded-lg border-2 border-dashed border-slate-300 hover:border-cyan-400 flex flex-col items-center justify-center cursor-pointer text-slate-500 hover:text-cyan-600 transition ${(uploading || photos.length >= 6) ? "opacity-40 pointer-events-none" : ""}`} data-testid="leasing-report-gallery-label">
                    <Camera size={20}/>
                    <div className="text-[10px] font-black uppercase tracking-widest mt-1">Photo roll</div>
                    <input type="file" accept="image/*" multiple
                      className="hidden" onChange={e => uploadFiles(e.target.files)}
                      data-testid="leasing-report-gallery-input"/>
                  </label>
                </div>
                {uploading && <div className="text-[10px] text-cyan-600 italic mb-1">Uploading…</div>}
                {photos.length > 0 && (
                  <div className="grid grid-cols-3 gap-1">
                    {photos.map((p, i) => (
                      <div key={p.path} className="relative aspect-square rounded overflow-hidden bg-slate-100 group" data-testid={`leasing-report-photo-${i}`}>
                        <img src={p.previewUrl} alt="" className="w-full h-full object-cover"/>
                        <button onClick={() => setPhotos(prev => prev.filter(x => x.path !== p.path))}
                          className="absolute top-0.5 right-0.5 h-5 w-5 rounded-full bg-red-600 hover:bg-red-700 text-white text-[10px] font-black opacity-0 group-hover:opacity-100 transition"
                          data-testid={`leasing-report-photo-remove-${i}`}>×</button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="flex items-center gap-2 pt-1">
                <div className="text-[10px] uppercase font-black tracking-widest text-slate-500">Priority</div>
                {["normal", "high", "urgent"].map(p => (
                  <button key={p} onClick={() => setPriority(p)}
                    className={`h-8 px-3 text-[11px] font-black uppercase tracking-wider rounded ${priority === p ? (p === "urgent" ? "bg-red-600 text-white" : p === "high" ? "bg-orange-500 text-white" : "bg-cyan-600 text-white") : "bg-slate-100 text-slate-600"}`}
                    data-testid={`leasing-report-priority-${p}`}>{p}</button>
                ))}
              </div>
              <div className="flex gap-2 mt-3">
                <button onClick={onClose} className="flex-1 bg-slate-100 rounded-lg py-2.5 font-bold text-sm">Cancel</button>
                <button onClick={submit} disabled={busy || uploading}
                  className="flex-1 bg-orange-600 hover:bg-orange-700 text-white rounded-lg py-2.5 font-black text-sm disabled:opacity-50"
                  data-testid="leasing-report-submit">
                  {busy ? "Submitting…" : "Submit report"}
                </button>
              </div>
            </div>
          </>
        ) : (
          <>
            <div className="text-center py-4" data-testid="leasing-report-done">
              <CheckCircle weight="fill" size={48} className="text-emerald-500 mx-auto"/>
              <div className="font-black text-lg mt-2">Sent to office</div>
              <div className="text-sm text-slate-500 mt-1">
                {done.wo_id ? "A work order was opened and PMR + the owner have been notified." : "The office and owner have been notified."}
                {done.photos?.length ? ` ${done.photos.length} photo${done.photos.length === 1 ? "" : "s"} attached.` : ""}
              </div>
              <button onClick={onDone} className="mt-4 w-full bg-slate-900 text-white font-black py-2.5 rounded-lg text-sm">Done</button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
